import * as CacheManager from "cache-manager";
import { StoryblokClient as StoryblokApiClient } from "@storyblok/react";

import {
  IStoryBlokAnnouncementBanner,
  IStoryBlokCookieBar,
  IStoryBlokHeader,
  IStoryBlokNewsletterSignUpModal,
  IStoryBlokSeo,
} from "../components/page/api.types";
import { IStoryBlokArticleMain } from "../types/article/IStoryBlokArticleMain";
import { RevalidationTime } from "../constants/revalidation";

import { ARTICLE_RELATIONS, sbParams } from "./config";
import { getStoryblokApi } from "./get-storyblok-api";

class StoryblokClient {

  // Resolved per call, not at module load, so this never pins a stale client.
  private get api(): StoryblokApiClient {
    return getStoryblokApi();
  }

  private readonly cachePromise = CacheManager.caching("memory", {
    ttl: RevalidationTime.STANDARD,
  });

  public async getDefaultHeader() {
    const header: IStoryBlokHeader = await this.getStoryContent("reusable/page/header");
    return header;
  }

  public async getDefaultCookieBar() {
    const cookieBar: IStoryBlokCookieBar = await this.getStoryContent("reusable/page/cookie-bar");
    return cookieBar;
  }

  public async getDefaultAnnouncementBanner() {
    const cookieBar: IStoryBlokAnnouncementBanner = await this.getStoryContent("reusable/page/announcement-banner");
    return cookieBar;
  }

  public async getFallbackHeaderTags() {
    const fallbackTags: IStoryBlokSeo = await this.getStoryContent("globals/default-header-tags");
    return fallbackTags;
  }

  public async getNewsletterForm() {
    const newsletterSignupModal: IStoryBlokNewsletterSignUpModal = await this.getStoryContent("reusable/page/newsletter-form");
    return newsletterSignupModal;
  }

  public async getBlogPosts(page = 1) {
    try {
      const response = await this.api?.get(
        `cdn/stories?starts_with=articles/posts/&filter_query[component][not_in]=author,category&sort_by=first_published_at:desc&cv=${Date.now()}`,
        {
          ...sbParams,
          resolve_relations: ARTICLE_RELATIONS,
          per_page: 12,
          page,
        },
      );

      if (!response?.data) {
        return {
          articles: [],
          total: 0,
          perPage: 0,
        };
      }

      const articles = response.data.stories as IStoryBlokArticleMain[];

      return {
        articles,
        total: response.total,
        perPage: response.perPage,
      };
    } catch {
      return {
        articles: [],
        total: 0,
        perPage: 0,
      };
    }
  }

  private async getStoryContent(path: string, disableCache = false) {

    const cache = await this.cachePromise;

    if (!disableCache) {
      const cacheResult = await cache.get(path);
      if (cacheResult) return cacheResult;
    }

    const response = await this.api?.get(`cdn/stories/${path}`, sbParams);
    if (!response?.data?.story) return;
    const { content } = response.data.story;

    if (!disableCache) {
      await cache.set(path, content);
    }

    return content;
  }

}

export const SBHelper = new StoryblokClient();
